import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Collision } from '../../models/collision';
import { AxiosInstance } from 'axios';
import { RootState } from '../store';
import { ReportStatus } from './configurationSlice';

export interface CollisionState {
  collision: Collision;
  collisions: Collision[];
  isLoading: boolean;
  error: string | null;
}

const initialState: CollisionState = {
  collision: {
    animalType: null,
    witnessOrCameraAvailable: null,
    location: undefined,
    wasHitAndRun: undefined,
    isEligible: undefined,
    eligibility: [],
    metadata: {
      formState: [
        {
          step: 1,
          name: 'Collision Type',
          nav: '#collision-identification',
          completed: false,
          inProgress: true, // step one is started by default
          skipped: false,
          substeps: [],
        },
        {
          step: 2,
          name: 'Location',
          nav: '#location-check',
          completed: false,
          inProgress: false,
          skipped: false,
          substeps: [],
        },
        {
          step: 3,
          name: 'Date & Time',
          nav: '#date-time',
          completed: false,
          inProgress: false,
          skipped: false,
          substeps: [],
        },
        {
          step: 4,
          name: 'Direction of Travel',
          nav: '#direction-road-details-introduction',
          completed: false,
          inProgress: false,
          skipped: false,
          substeps: [],
        },

        {
          step: 5,
          name: 'Vehicle Damage',
          nav: '#damage',
          completed: false,
          inProgress: false,
          skipped: false,
          substeps: [],
        },
        {
          step: 6,
          name: 'Media Upload',
          nav: '#media-introduction',
          completed: false,
          inProgress: false,
          skipped: false,
          substeps: [],
        },
        {
          step: 7,
          name: 'Contributing Factors',
          nav: '#contributing-factors-introduction',
          completed: false,
          inProgress: false,
          skipped: false,
          substeps: [],
        },
        {
          step: 8,
          name: 'Driver & Vehicle',
          nav: '#driver-vehicle-introduction',
          completed: false,
          inProgress: false,
          skipped: false,
          substeps: [],
        },
        {
          step: 9,
          name: 'Passenger & Safety',
          nav: '#passenger-and-safety-info',
          completed: false,
          inProgress: false,
          skipped: false,
          substeps: [],
        },
        {
          step: 10,
          name: 'Collision Review',
          nav: '#review',
          completed: false,
          inProgress: false,
          skipped: false,
          substeps: [],
        },
      ],
    },
  },
  collisions: [],
  isLoading: false,
  error: null,
};

export const publishCollision = createAsyncThunk(
  'collision/publishCollision',
  async (
    {
      csrService,
    }: {
      csrService: AxiosInstance;
    },
    { getState },
  ) => {
    const state = getState() as RootState;
    const collisionUrl = '/api/v2/collisions';
    if (state.collision && state.collision.collision) {
      let collision: any = null;

      try {
        if (
          !state.collision.collision.id ||
          state.collision.collision.id === 0
        ) {
          collision = await csrService.post(
            collisionUrl,
            state.collision.collision,
          );
        } else {
          collision = await csrService.put(
            `${collisionUrl}/${state.collision.collision.id}`,
            state.collision.collision,
          );
        }

        const returnCollision = await csrService.get(
          `${collisionUrl}/${collision.data.id}`,
        );

        console.log('state.collision.collision', state.collision.collision);
        console.log('Thunk response data:', returnCollision.data);

        return returnCollision.data;
      } catch (error) {
        console.error('Error in publishCollision :', error);
        throw error;
      }
    }

    return null;
  },
);

export const submitCollision = createAsyncThunk(
  'collision/submitCollision',
  async (
    {
      csrService,
    }: {
      csrService: AxiosInstance;
    },
    { getState },
  ) => {
    const state = getState() as RootState;
    if (state.collision && state.collision.collision) {
      let collision: any = null;

      try {
        if (
          state.collision.collision.reportStatus &&
          state.collision.collision.reportStatus == 2
        ) {
          const collisionUrl = `/api/v2/collisions/${state.collision.collision.id}/status`;
          collision = await csrService.post(
            collisionUrl,
            state.collision.collision,
          );
        }

        const returnCollision = await csrService.get(
          `/api/v2/collisions/${state.collision.collision.id}`,
        );

        console.log('state.collision.collision', state.collision.collision);
        console.log('Thunk response data:', returnCollision.data);

        return returnCollision.data;
      } catch (error) {
        console.error('Error in submitCollision :', error);
        throw error;
      }
    }

    return null;
  },
);

export const fetchCollisionById = createAsyncThunk(
  'collision/fetchCollisionById',
  async (
    { id, csrService }: { id: string; csrService: AxiosInstance },
    { rejectWithValue },
  ) => {
    try {
      const response = await csrService.get(`/api/v2/collisions/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchCollisionsByEmail = createAsyncThunk(
  'collision/fetchCollisionsByEmail',
  async (
    {
      username,
      csrService,
      reportStatus,
      lastUpdatedWithinDays,
    }: {
      username: string;
      csrService: AxiosInstance;
      reportStatus?: number[];
      lastUpdatedWithinDays?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      let url = '/api/v2/collisions';

      let queryString = `?username=${encodeURIComponent(username)}`;

      if (reportStatus && reportStatus.length > 0) {
        reportStatus.forEach((reportStatusValue) => {
          queryString += `&reportStatus=${encodeURIComponent(String(reportStatusValue))}`;
        });
      }

      if (lastUpdatedWithinDays !== undefined) {
        queryString += `&lastUpdatedWithinDays=${encodeURIComponent(String(lastUpdatedWithinDays))}`;
      }

      console.log('queryString', queryString);

      const response = await csrService.get(url + queryString);
      console.log('response', response);
      return { status: response.status, data: response.data };
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return { status: 404, data: [] };
      }

      return rejectWithValue(error.message);
    }
  },
);

//TODO: Should be blocked on UAT and PROD
export const deleteCollisionById = createAsyncThunk(
  'collision/deleteCollisionById',
  async (
    { id, csrService }: { id: string; csrService: AxiosInstance },
    { rejectWithValue },
  ) => {
    try {
      const response = await csrService.delete(`/api/v2/collisions/${id}`);
      return { status: response.status, data: response.data };
    } catch (error: any) {
      if (error.response && error.response.status) {
        return { status: error.response.status, data: [] };
      }
      return rejectWithValue(error.message);
    }
  },
);

export const validateDriver = createAsyncThunk(
  'validate/driver',
  async (
    { csrService, collision }: { csrService: AxiosInstance; collision: any },
    { rejectWithValue },
  ) => {
    try {
      const response = await csrService.post(
        `/api/v1/validate/driver`,
        collision,
      );
      return response.data; // Assuming the API returns validation results
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const validateVehicle = createAsyncThunk(
  'validate/vehicle',
  async (
    { csrService, collision }: { csrService: AxiosInstance; collision: any },
    { rejectWithValue },
  ) => {
    try {
      const response = await csrService.post(
        `/api/v1/validate/vehicle`,
        collision,
      );
      return response.data; // Assuming the API returns validation results
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const validateCollision = createAsyncThunk(
  'validate/collision',
  async (
    { csrService, collision }: { csrService: AxiosInstance; collision: any },
    { rejectWithValue },
  ) => {
    try {
      const response = await csrService.post(
        `/api/v1/validate/collision`,
        collision,
      );
      return { status: response.status, data: response.data };
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue({
          status: error.response.status,
          data: error.response.data,
        });
      }
      return rejectWithValue({ status: 500, data: error.message });
    }
  },
);

export const checkLocationStatus = createAsyncThunk(
  'location/checkActiveStatus',
  async (
    {
      csrService,
      lat,
      lng,
    }: { csrService: AxiosInstance; lat: number; lng: number },
    { rejectWithValue },
  ) => {
    try {
      let queryString = `?latitude=${encodeURIComponent(lat)}&longitude=${encodeURIComponent(lng)}`;
      const response = await csrService.get(
        `/api/v2/is-active-location` + queryString,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const downloadDamageStickerPdf = createAsyncThunk(
  'collision/downloadDamageStickerPdf',
  async (
    {
      csrService,
      collisionId,
    }: {
      csrService: AxiosInstance;
      collisionId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await csrService.get(
        `/api/v2/collisions/${collisionId}/damage-sticker/download`,
        {
          responseType: 'blob', // Important for file downloads
        },
      );

      // Extract filename from headers (optional, defaults to a fallback name)
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'damage_sticker.pdf';
      if (contentDisposition) {
        const matches = /filename="?(.+?)"?$/i.exec(contentDisposition);
        if (matches && matches[1]) {
          fileName = matches[1];
        }
      }

      // Create a link and trigger a download in the browser
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);

      return true; // Indicate success
    } catch (error: any) {
      console.error('Error in downloadDamageStickerPdf:', error);
      return rejectWithValue(error.message);
    }
  },
);

const collisionSlice = createSlice({
  name: 'collision',
  initialState,
  reducers: {
    clearCollision(state) {
      state.collision = initialState.collision;
      state.collisions = [];
      state.error = null;
      state.isLoading = false;
      return state;
    },

    updateCollision(state, action: PayloadAction<Collision>) {
      state.collision = {
        ...state.collision,
        ...action.payload,
      };
      console.log('state.collision', state.collision);
      return state;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(publishCollision.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        publishCollision.fulfilled,
        (state, action: PayloadAction<Collision>) => {
          if (action.payload) {
            state.collision = { ...action.payload };
            state.isLoading = false;
            state.error = null;

            console.log('payload id', action.payload.id);
          }
        },
      )
      .addCase(
        publishCollision.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload;
        },
      )
      .addCase(fetchCollisionById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchCollisionById.fulfilled,
        (state, action: PayloadAction<Collision>) => {
          state.collision = action.payload;
          state.isLoading = false;
        },
      )
      .addCase(
        fetchCollisionById.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload;
        },
      )
      .addCase(fetchCollisionsByEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchCollisionsByEmail.fulfilled,
        (
          state,
          action: PayloadAction<{ status: number; data: Collision[] }>,
        ) => {
          state.collisions = action.payload.data;
          state.isLoading = false;
        },
      )
      .addCase(
        fetchCollisionsByEmail.rejected,
        (state, action: PayloadAction<any>) => {
          if (Array.isArray(action.payload) && action.payload.length === 0) {
            state.collisions = [];
          } else {
            state.error = action.payload;
          }
          state.isLoading = false;
        },
      );
  },
});

export const { updateCollision, clearCollision } = collisionSlice.actions;
export { initialState };
export default collisionSlice.reducer;
